import type { OrgType } from './persona'
import type { HuntType } from './hunt-type'
import type { LocationOccupancyMode } from './properties'

export type TimePeriod = 'am' | 'midday' | 'pm' | 'all-day' | 'night'

export type BookingStatus = 'draft' | 'confirmed' | 'completed' | 'cancelled' | 'no_show'

export const MAX_BOOKING_DURATION_DAYS = 60

export const BOOKING_SORTS = [
  'hunt_date_asc',
  'hunt_date_desc',
  'created_desc',
  'amount_desc',
  'party_asc',
] as const

export type BookingSort = (typeof BOOKING_SORTS)[number]

export type PaymentStatus =
  | 'unpaid'
  | 'deposit_due'
  | 'deposit_paid'
  | 'balance_due'
  | 'paid_in_full'
  | 'partially_refunded'
  | 'refunded'
  | 'overdue'

export type BookingDailyEntry = {
  day: number
  timePeriod?: TimePeriod
  blindId?: string
  zoneId?: string
  standId?: string
  customLocationId?: string
  blindName?: string | null
  zoneName?: string | null
  standName?: string | null
  customLocationName?: string | null
}

export type BookingDailyEntryInput = BookingDailyEntry & {
  guideIds?: string[]
  dogIds?: string[]
}

export type BookingGuide = {
  id: string
  guideId: string
  guideName?: string
  dayDate?: string
}

export type BookingDog = {
  id: string
  dogId: string
  dogName?: string
  dayDate?: string
}

export type Booking = {
  id: string
  orgId: string
  orgType: OrgType
  huntId?: string
  partyName: string
  status: BookingStatus
  huntDate: string
  durationDays?: number
  timePeriod?: TimePeriod
  effectiveStartTime?: string
  effectiveEndTime?: string
  blindId?: string
  standId?: string
  zoneId?: string
  customLocationId?: string
  dogId?: string
  bookedBy: string
  notes?: string
  packageId?: string
  huntType?: HuntType
  totalAmount?: number
  remainingBalance?: number
  depositAmount?: number
  sendDeposit?: boolean
  depositDueDate?: string
  balanceDueDate?: string
  paymentStatus: PaymentStatus
  hasUnresolvedProviderVoidFailure?: boolean
  plannedPartySize: number
  createdAt: string
  cancellationReason?: string
  updatedAt: string
  blindName?: string
  zoneName?: string
  propertyId?: string
  guideIds?: string[]
  guideNames?: string[]
  guides?: BookingGuide[]
  dogIds?: string[]
  dogs?: BookingDog[]
  dailyTimes?: BookingDailyEntry[]
  excludedDates?: string[]
  dogName?: string
  huntStatus?: string
  maxGuests?: number
  packageName?: string
  packagePrice?: number
  packagePricingMode?: PricingMode
  openToMembers?: boolean
}

export type BookingParticipantStatus = 'placeholder' | 'invited' | 'active'

export type BookingParticipantInvoice = {
  id: string
  status: InvoiceStatus
  amount: number
  dueDate?: string
  stripePaymentLinkUrl?: string
  checkoutMode?: InvoiceCheckoutMode
  payUrl?: string
}

export type BookingInvoiceSummary = {
  id: string
  status: string
  amount: number
  amountPaid: number
}

export type BookingParticipant = {
  id: string
  bookingId: string
  userId?: string
  membershipId?: string
  name: string
  email?: string
  isHost: boolean
  status: BookingParticipantStatus
  addedByParticipantId?: string
  createdAt: string
  invoice?: BookingParticipantInvoice
}

export type BookingWithParticipants = Booking & {
  participants: BookingParticipant[]
  occupancy?: number
  spotsRemaining?: number | null
}

export type CalendarDayBooking = {
  bookingId: string
  spotsRemaining: number | null
  openToMembers: boolean
  status: BookingStatus
}

export type CalendarDay = {
  date: string
  bookingCount: number
  bookedSeats: number
  totalCapacity?: number
  remainingSeats?: number
  draftCount: number
  noGuideCount: number
  overCapacityBlindCount: number
  unassignedPlaceholderCount: number
  guideConflictCount: number
  bookings?: CalendarDayBooking[]
}

export type CalendarDayAvailability = 'available' | 'limited' | 'full' | 'none'

export type CalendarDayData = {
  date: string
  availability: CalendarDayAvailability
  badgeValue: number
  hasAttention: boolean
  hasBookings: boolean
  hasConfirmedBookings: boolean
  hasDraftBookings: boolean
  totalCapacity?: number
  confirmedSeats?: number
}

export function deriveConfirmedSeats(
  day: Pick<CalendarDay, 'totalCapacity' | 'remainingSeats'>,
): number | undefined {
  return day.totalCapacity != null && day.remainingSeats != null
    ? day.totalCapacity - day.remainingSeats
    : undefined
}

export type BlindAvailability = {
  blindId: string
  name: string
  capacity: number
  bookedSeats: number
  remainingSeats: number
  checkDate?: string
}

export type ZoneAvailability = {
  zoneId: string
  name: string
  capacity: number | null
  bookedSeats: number
  remainingSeats: number | null
  checkDate?: string
}

export type StandAvailability = {
  standId: string
  name: string
  capacity: number | null
  bookedSeats: number
  remainingSeats: number | null
  checkDate?: string
}

export type InvoiceType = 'deposit' | 'balance' | 'full'

export type InvoiceStatus =
  | 'draft'
  | 'sent'
  | 'paid'
  | 'partially_paid'
  | 'void'
  | 'refunded'
  | 'uncollectible'
  | 'failed'
  | 'scheduled'

export type PaymentMethod = 'cash' | 'check' | 'other' | 'stripe'

export type EmailDeliveryStatus = 'not_attempted' | 'skipped' | 'delivered' | 'failed'

export type LineItem = {
  id?: string
  description: string
  quantity: number
  unitPrice: number
  position?: number
  packageId?: string
}

export type LineItemDraft = {
  description: string
  quantity?: number
  unitPrice?: number
  packageId?: string
}

export type InvoiceDisputeDisplayState = 'open' | 'under_review' | 'won' | 'lost' | 'closed'

export type InvoiceDispute = {
  status: string
  reason?: string
  amountCents: number
  currency: string
  evidenceDueBy?: string
  closedAt?: string
  displayState: InvoiceDisputeDisplayState
}

export type InvoiceAction = 'send' | 'resend' | 'mark_paid' | 'edit' | 'void' | 'refund'

export const SERVICE_FEE_RATE = 0.045

export const SERVICE_FEE_RATE_LABEL = `${+(SERVICE_FEE_RATE * 100).toFixed(2)}%`

export const CHECKOUT_FEE_NOTE =
  "Processing fees are added at checkout based on the customer's card"

export type InvoiceCheckoutMode = 'stripe_invoice' | 'bb_checkout'

export type InvoiceCheckoutFeeLine = {
  label: string
  amount: number
}

export type InvoicePaymentCard = {
  brand: string
  last4: string
  country?: string
  funding?: string
}

export type InvoiceCheckoutQuote = {
  amount: number
  fee: number
  total: number
  feeLines: InvoiceCheckoutFeeLine[]
  cardBrand: string
  cardLast4: string
  cardCountry?: string
  cardFunding?: string
  reusesOpenCheckout: boolean
}

export type Invoice = {
  id: string
  orgId: string
  orgType: OrgType
  bookingId?: string
  providerInvoiceId?: string
  participantUserId?: string
  invoiceType: InvoiceType
  paymentMethod: PaymentMethod | null
  amount: number
  platformFee: number
  amountPaid: number
  stripeProcessingFee?: number
  stripePaymentMethodType?: 'card' | 'us_bank_account'
  stripePaymentLinkUrl?: string
  checkoutMode?: InvoiceCheckoutMode
  payUrl?: string
  checkoutFeeBreakdown?: InvoiceCheckoutFeeLine[]
  paymentCard?: InvoicePaymentCard
  status: InvoiceStatus
  recipientEmail?: string
  clientName?: string
  dueDate?: string
  scheduledSendDate?: string
  notes?: string
  refundReason?: string
  failureReason?: string
  sentAt?: string
  paidAt?: string
  refundedAt?: string
  emailDeliveryStatus?: EmailDeliveryStatus
  emailDeliveryAttemptedAt?: string
  emailDeliveryError?: string
  providerVoidFailedAt?: string
  providerVoidError?: string
  providerVoidAcknowledgedAt?: string
  providerVoidAcknowledgedBy?: string
  providerVoidEscalatedAt?: string
  lineItems?: LineItem[]
  dispute?: InvoiceDispute
  availableActions: InvoiceAction[]
  createdAt: string
  updatedAt: string
}

export type PricingMode = 'flat' | 'per_guest' | 'per_day' | 'per_guest_day'

export type PackageType = 'external' | 'internal'

export function computePackageTotal(
  mode: PricingMode,
  price: number,
  guests: number,
  days: number,
): number {
  switch (mode) {
    case 'per_guest':
      return price * guests
    case 'per_day':
      return price * Math.max(days, 1)
    case 'per_guest_day':
      return price * guests * Math.max(days, 1)
    case 'flat':
    default:
      return price
  }
}

export type OutfitterPackage = {
  id: string
  name: string
  price: number
  isActive: boolean
  maxGuests?: number
  pricingMode?: PricingMode
  packageType?: PackageType
  description?: string
  orgId?: string
  orgType?: OrgType
  createdAt?: string
  updatedAt?: string
}

export type BlindOption = {
  id: string
  name: string
  capacity: number
  zoneId?: string
  isActive?: boolean
}

export type ZoneOption = {
  id: string
  name: string
  occupancyMode?: LocationOccupancyMode
  maxGroups?: number | null
  seatCapacity?: number | null
  isActive?: boolean
}

export type GuideOption = {
  id: string
  displayName: string
}

export type DogOption = {
  id: string
  name: string
}

export const BOOKING_PARTY_ROLES = ['client', 'member', 'guest', 'outfitter'] as const

export type BookingPartyRole = (typeof BOOKING_PARTY_ROLES)[number]

export function isBookingPartyRole(value: unknown): value is BookingPartyRole {
  return (BOOKING_PARTY_ROLES as readonly unknown[]).includes(value)
}

export function orderBookingPartyRoles(roles: readonly unknown[]): BookingPartyRole[] {
  return BOOKING_PARTY_ROLES.filter((role) => roles.includes(role))
}

export type ClientOption = {
  id: string
  membershipId: string
  userId?: string
  name: string
  email?: string
  isInvited: boolean
  role?: BookingPartyRole
}

export type ConnectStatus = {
  connected?: boolean
  chargesEnabled: boolean
  payoutsEnabled?: boolean
  detailsSubmitted?: boolean
  invoiceCheckoutEnabled?: boolean
}

export type InvoicingSettings = {
  depositPercentage?: number
  autoSendInvoices?: boolean
  balanceDueDaysBeforeHunt?: number
}
